import { Clock, Car, MapPin, Package } from "lucide-react";
import { formatMinutes } from "@/lib/format";
import { formatDuration } from "@/lib/geo-utils";

interface BlockStatsBarProps {
  /** Booked duration in minutes */
  durationMins: number;
  /** Actual duration in minutes (if different from booked) */
  actualMins?: number;
  /** Miles driven */
  miles: number | null;
  /** Source of mileage data */
  mileageSource?: "tracked" | "manual" | null;
  /** Number of stops */
  stops?: number;
  /** Number of locations */
  locations?: number;
  /** Number of packages */
  packages?: number;
  /** Tracked drive time in ms (from shift) */
  driveTimeMs?: number;
}

export function BlockStatsBar({
  durationMins,
  actualMins,
  miles,
  mileageSource,
  stops,
  packages,
  driveTimeMs,
}: BlockStatsBarProps) {
  const displayMins = actualMins ?? durationMins;

  return (
    <div className="flex flex-wrap items-center gap-3 py-2">
      {/* Duration */}
      <div className="flex items-center gap-1.5 text-sm">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{formatMinutes(displayMins)}</span>
        {actualMins && actualMins !== durationMins && (
          <span className="text-xs text-muted-foreground">
            ({formatMinutes(durationMins)} booked)
          </span>
        )}
      </div>

      {/* Miles */}
      {miles !== null && miles > 0 && (
        <div className="flex items-center gap-1.5 text-sm">
          <Car className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{miles.toFixed(1)} mi</span>
          {mileageSource && (
            <span className="text-xs text-muted-foreground">({mileageSource})</span>
          )}
        </div>
      )}

      {/* Drive time (if tracked) */}
      {driveTimeMs && driveTimeMs > 0 && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <span>{formatDuration(driveTimeMs)} drive</span>
        </div>
      )}

      {/* Stops */}
      {stops && (
        <div className="flex items-center gap-1.5 text-sm">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{stops} stops</span>
        </div>
      )}

      {/* Packages */}
      {packages && (
        <div className="flex items-center gap-1.5 text-sm">
          <Package className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{packages} pkg</span>
        </div>
      )}

      {/* Packages per hour */}
      {packages && durationMins > 0 && (
        <div className="text-xs text-muted-foreground">
          ({(packages / (durationMins / 60)).toFixed(1)}/hr)
        </div>
      )}
    </div>
  );
}
